/**
 * Tournament context provider for state management.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

/** Mobile breakpoint - matches use-mobile.ts */
const MOBILE_BREAKPOINT = 768;

/** Countries per group: 3 for mobile, 6 for desktop */
const MOBILE_GROUP_SIZE = 3;
const DESKTOP_GROUP_SIZE = 6;
import { getCountriesForScope } from '@/data/countries';
import type { CountryCode } from '@/data/countries/types';
import {
  type TournamentState,
  type TournamentContextValue,
  type TournamentGroup,
  type BracketMatch,
  type AdvancingCountry,
  type EliminatedCountry,
  type GroupCountryRating,
  seedGroups,
  reseedForNextRound,
  processGroupResults,
  generateBracket,
  recordMatchResult,
  getNextMatchIndex,
  isBracketComplete,
  getBracketRankings,
  calculateFinalRankings,
  rankingsToRatings,
  saveTournamentState,
  loadTournamentState,
  clearTournamentState,
  clearAllDraftRatings,
} from '@/lib/tournament';

const TournamentContext = createContext<TournamentContextValue | null>(null);

interface TournamentProviderProps {
  children: ReactNode;
  scopeId: string;
  onRatingsComplete: (ratings: Record<string, number>) => void;
}

/**
 * Generate a unique tournament ID.
 */
function generateTournamentId(): string {
  return `tournament-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Check if we should transition to bracket stage.
 * Transition when 16 or fewer countries remain.
 */
function shouldTransitionToBracket(advancingCount: number): boolean {
  return advancingCount <= 16;
}

export function TournamentProvider({
  children,
  scopeId,
  onRatingsComplete,
}: TournamentProviderProps) {
  // Initialize from localStorage if there's saved state for this scope
  const [state, setState] = useState<TournamentState | null>(() => {
    return loadTournamentState(scopeId);
  });

  // Capture initial mobile state (doesn't change on resize)
  const groupSizeRef = useRef<number>(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
      ? MOBILE_GROUP_SIZE
      : DESKTOP_GROUP_SIZE
  );

  // Save state to localStorage on changes
  useEffect(() => {
    if (state) {
      saveTournamentState(state);
    }
  }, [state]);

  // Load saved state when scope changes
  useEffect(() => {
    const savedState = loadTournamentState(scopeId);
    setState(savedState);
  }, [scopeId]);

  // Start a new tournament
  const startTournament = useCallback(() => {
    const countries = getCountriesForScope(scopeId);
    const countryCodes = countries.map(c => c.code);

    if (countryCodes.length < 3) {
      console.warn('Not enough countries to start tournament');
      return;
    }

    // Clear any draft ratings from previous tournaments
    clearAllDraftRatings(scopeId);

    const now = Date.now();

    // Check if we need filter stage (>32 countries)
    if (countryCodes.length > 32) {
      // Start with filter stage
      setState({
        id: generateTournamentId(),
        scopeId,
        status: 'filter_stage',
        totalCountries: countryCodes.length,
        originalCountries: countryCodes,
        filterSelection: [],
        groups: [],
        currentGroupIndex: 0,
        groupRound: 0,
        eliminatedInGroups: [],
        advancingCountries: [],
        bracket: null,
        currentMatchIndex: 0,
        finalRankings: null,
        createdAt: now,
        updatedAt: now,
      });
      return;
    }

    // Initialize advancing countries with default values
    const advancingCountries: AdvancingCountry[] = countryCodes.map((code, idx) => ({
      code,
      stars: 3, // Default middle rating
      tiebreakPosition: idx + 1,
      ratedInRound: 0,
      points: 3, // Legacy compatibility
    }));

    // Check if we should skip group stage entirely
    if (countryCodes.length <= 16) {
      // Go directly to bracket
      const bracket = generateBracket(advancingCountries);

      setState({
        id: generateTournamentId(),
        scopeId,
        status: 'bracket_stage',
        totalCountries: countryCodes.length,
        groups: [],
        currentGroupIndex: 0,
        groupRound: 0,
        eliminatedInGroups: [],
        advancingCountries,
        bracket,
        currentMatchIndex: getNextMatchIndex(bracket),
        finalRankings: null,
        createdAt: now,
        updatedAt: now,
      });
    } else {
      // Start with group stage - use mobile/desktop group size
      const groups = seedGroups(countryCodes, groupSizeRef.current);

      setState({
        id: generateTournamentId(),
        scopeId,
        status: 'group_stage',
        totalCountries: countryCodes.length,
        groups,
        currentGroupIndex: 0,
        groupRound: 1,
        eliminatedInGroups: [],
        advancingCountries,
        bracket: null,
        currentMatchIndex: 0,
        finalRankings: null,
        createdAt: now,
        updatedAt: now,
      });
    }
  }, [scopeId]);

  // Toggle country selection in filter stage
  const toggleFilterCountry = useCallback((code: CountryCode) => {
    setState(prev => {
      if (!prev || prev.status !== 'filter_stage') return prev;
      const selection = new Set(prev.filterSelection ?? []);
      if (selection.has(code)) {
        selection.delete(code);
      } else {
        selection.add(code);
      }
      return {
        ...prev,
        filterSelection: Array.from(selection),
        updatedAt: Date.now(),
      };
    });
  }, []);

  // Set multiple countries selection at once (for select all / deselect all)
  const setFilterCountries = useCallback((codes: CountryCode[], selected: boolean) => {
    setState(prev => {
      if (!prev || prev.status !== 'filter_stage') return prev;
      const selection = new Set(prev.filterSelection ?? []);
      const validCodes = new Set(prev.originalCountries ?? []);

      for (const code of codes) {
        if (!validCodes.has(code)) continue;
        if (selected) {
          selection.add(code);
        } else {
          selection.delete(code);
        }
      }
      return {
        ...prev,
        filterSelection: Array.from(selection),
        updatedAt: Date.now(),
      };
    });
  }, []);

  // Complete filter stage and proceed to groups or bracket
  const completeFilterStage = useCallback(() => {
    setState(prev => {
      if (!prev || prev.status !== 'filter_stage') return prev;
      const selected = prev.filterSelection ?? [];

      if (selected.length < 8) return prev; // Minimum validation

      // Initialize advancing countries from selection
      const advancingCountries: AdvancingCountry[] = selected.map((code, idx) => ({
        code,
        stars: 3,
        tiebreakPosition: idx + 1,
        ratedInRound: 0,
        points: 3,
      }));

      const now = Date.now();

      if (selected.length <= 16) {
        // Skip groups, go directly to bracket
        const bracket = generateBracket(advancingCountries);
        return {
          ...prev,
          status: 'bracket_stage' as const,
          totalCountries: selected.length,
          advancingCountries,
          bracket,
          currentMatchIndex: getNextMatchIndex(bracket),
          originalCountries: undefined,
          filterSelection: undefined,
          updatedAt: now,
        };
      } else {
        // Go to group stage
        const groups = seedGroups(selected, groupSizeRef.current);
        return {
          ...prev,
          status: 'group_stage' as const,
          totalCountries: selected.length,
          groups,
          groupRound: 1,
          advancingCountries,
          originalCountries: undefined,
          filterSelection: undefined,
          updatedAt: now,
        };
      }
    });
  }, []);

  // Submit group star ratings (new approach)
  const submitGroupRating = useCallback((ratings: GroupCountryRating[]) => {
    if (!state || state.status !== 'group_stage') return;

    const currentGroup = state.groups[state.currentGroupIndex];
    if (!currentGroup) return;

    // Update group with ratings
    const updatedGroups = [...state.groups];
    updatedGroups[state.currentGroupIndex] = {
      ...currentGroup,
      ratings,
      // Also set rankings for compatibility (sorted by stars desc, then tiebreak)
      rankings: ratings
        .sort((a, b) => b.stars - a.stars || a.tiebreakPosition - b.tiebreakPosition)
        .map(r => r.code),
    };

    // Update advancing countries with their ratings
    // (We keep everyone as "advancing" until all groups are done, then select top 16)
    const groupCodes = new Set(currentGroup.countries);
    const otherAdvancing = state.advancingCountries.filter(
      c => !groupCodes.has(c.code)
    );

    // Add this group's countries with their ratings
    const thisGroupAdvancing: AdvancingCountry[] = ratings.map(r => ({
      code: r.code,
      stars: r.stars,
      tiebreakPosition: r.tiebreakPosition,
      ratedInRound: state.groupRound,
      points: r.stars, // Legacy compatibility
    }));

    const newAdvancing = [...otherAdvancing, ...thisGroupAdvancing];

    // Check if we've finished all groups
    const nextGroupIndex = state.currentGroupIndex + 1;

    if (nextGroupIndex >= state.groups.length) {
      // All groups complete! Now select top 16 for bracket
      // Sort by stars (desc), then tiebreakPosition (asc)
      const sorted = [...newAdvancing].sort((a, b) => {
        if (b.stars !== a.stars) return b.stars - a.stars;
        return a.tiebreakPosition - b.tiebreakPosition;
      });

      // Top 16 advance to bracket, rest are eliminated
      const bracketAdvancing = sorted.slice(0, 16);
      const eliminated: EliminatedCountry[] = sorted.slice(16).map((c, idx) => ({
        code: c.code,
        stars: c.stars,
        tiebreakPosition: c.tiebreakPosition,
        eliminatedInRound: state.groupRound,
        points: c.stars,
        groupPosition: 16 + idx + 1, // Position after bracket cut
      }));

      // Generate bracket with top 16
      const bracket = generateBracket(bracketAdvancing);

      setState({
        ...state,
        status: 'bracket_stage',
        groups: updatedGroups,
        advancingCountries: bracketAdvancing,
        eliminatedInGroups: [...state.eliminatedInGroups, ...eliminated],
        bracket,
        currentMatchIndex: getNextMatchIndex(bracket),
        updatedAt: Date.now(),
      });
    } else {
      // Move to next group
      setState({
        ...state,
        groups: updatedGroups,
        currentGroupIndex: nextGroupIndex,
        advancingCountries: newAdvancing,
        updatedAt: Date.now(),
      });
    }
  }, [state]);

  // Legacy: Submit group ranking (deprecated, use submitGroupRating instead)
  const submitGroupRanking = useCallback((rankings: CountryCode[]) => {
    // Convert old format to new format (assign stars based on position)
    const ratings: GroupCountryRating[] = rankings.map((code, idx) => ({
      code,
      stars: Math.max(0, 5 - idx), // 1st=5, 2nd=4, etc.
      tiebreakPosition: idx + 1,
    }));
    submitGroupRating(ratings);
  }, [submitGroupRating]);

  // Navigate to a specific group (for flicking through groups)
  const goToGroup = useCallback((index: number) => {
    if (!state || state.status !== 'group_stage') return;
    if (index < 0 || index >= state.groups.length) return;

    setState({
      ...state,
      currentGroupIndex: index,
      updatedAt: Date.now(),
    });
  }, [state]);

  // Submit match result
  const submitMatchResult = useCallback((winner: CountryCode) => {
    if (!state || state.status !== 'bracket_stage' || !state.bracket) return;

    const currentMatch = state.bracket[state.currentMatchIndex];
    if (!currentMatch) return;

    // Record result
    const updatedBracket = recordMatchResult(
      state.bracket,
      currentMatch.id,
      winner
    );

    // Check if bracket is complete
    if (isBracketComplete(updatedBracket)) {
      // Tournament complete!
      const bracketRankings = getBracketRankings(updatedBracket);
      const finalRankings = calculateFinalRankings(
        bracketRankings,
        state.eliminatedInGroups,
        state.groupRound,
        state.totalCountries
      );

      setState({
        ...state,
        status: 'completed',
        bracket: updatedBracket,
        finalRankings,
        updatedAt: Date.now(),
      });
    } else {
      // Move to next match
      const nextMatchIndex = getNextMatchIndex(updatedBracket);

      // Dev logging to help debug "no match to decide" issues
      if (import.meta.env.DEV && nextMatchIndex === -1) {
        console.warn('[Tournament] No next match found. Bracket state:', {
          totalMatches: updatedBracket.length,
          decidedMatches: updatedBracket.filter(m => m.winner !== null).length,
          readyMatches: updatedBracket.filter(m => m.winner === null && m.countryA !== '' && m.countryB !== '').length,
          waitingMatches: updatedBracket.filter(m => m.winner === null && (m.countryA === '' || m.countryB === '')).length,
          matches: updatedBracket.map(m => ({
            id: m.id,
            round: m.round,
            countryA: m.countryA || '(empty)',
            countryB: m.countryB || '(empty)',
            winner: m.winner || '(none)',
          })),
        });
      }

      setState({
        ...state,
        bracket: updatedBracket,
        currentMatchIndex: nextMatchIndex,
        updatedAt: Date.now(),
      });
    }
  }, [state]);

  // Abandon tournament
  const abandonTournament = useCallback(() => {
    clearTournamentState(scopeId);
    setState(null);
  }, [scopeId]);

  // Apply ratings
  const applyRatings = useCallback(() => {
    if (!state || !state.finalRankings) return;

    const ratings = rankingsToRatings(state.finalRankings);
    onRatingsComplete(ratings);
  }, [state, onRatingsComplete]);

  // Computed values
  const currentGroup = useMemo((): TournamentGroup | null => {
    if (!state || state.status !== 'group_stage') return null;
    return state.groups[state.currentGroupIndex] ?? null;
  }, [state]);

  const currentMatch = useMemo((): BracketMatch | null => {
    if (!state || state.status !== 'bracket_stage' || !state.bracket) return null;
    return state.bracket[state.currentMatchIndex] ?? null;
  }, [state]);

  const progress = useMemo(() => {
    if (!state) {
      return {
        phase: 'not_started' as const,
        groupRound: 0,
        currentGroup: 0,
        totalGroups: 0,
        bracketRound: 0,
        currentMatch: 0,
        totalMatches: 0,
        countriesRemaining: 0,
      };
    }

    // Calculate countries remaining in bracket based on current round
    let countriesRemaining = state.advancingCountries.length;
    if (state.status === 'bracket_stage' && state.bracket) {
      const currentRound = state.bracket[state.currentMatchIndex]?.round ?? 1;
      const totalRounds = Math.max(...state.bracket.map(m => m.round));
      // At round R with totalRounds T, remaining = 2^(T - R + 1)
      // e.g., at Final (R=T), remaining = 2; at SF (R=T-1), remaining = 4
      countriesRemaining = 2 ** (totalRounds - currentRound + 1);
    }

    return {
      phase: state.status,
      groupRound: state.groupRound,
      currentGroup: state.currentGroupIndex + 1,
      totalGroups: state.groups.length,
      bracketRound: state.bracket?.[state.currentMatchIndex]?.round ?? 0,
      currentMatch: state.currentMatchIndex + 1,
      totalMatches: state.bracket?.length ?? 0,
      countriesRemaining,
    };
  }, [state]);

  const contextValue: TournamentContextValue = useMemo(
    () => ({
      state,
      actions: {
        startTournament,
        toggleFilterCountry,
        setFilterCountries,
        completeFilterStage,
        submitGroupRating,
        submitGroupRanking,
        goToGroup,
        submitMatchResult,
        abandonTournament,
        applyRatings,
      },
      currentGroup,
      currentMatch,
      progress,
    }),
    [
      state,
      startTournament,
      toggleFilterCountry,
      setFilterCountries,
      completeFilterStage,
      submitGroupRating,
      submitGroupRanking,
      goToGroup,
      submitMatchResult,
      abandonTournament,
      applyRatings,
      currentGroup,
      currentMatch,
      progress,
    ]
  );

  return (
    <TournamentContext.Provider value={contextValue}>
      {children}
    </TournamentContext.Provider>
  );
}

/**
 * Hook to access tournament context.
 */
export function useTournament(): TournamentContextValue {
  const context = useContext(TournamentContext);
  if (!context) {
    throw new Error('useTournament must be used within TournamentProvider');
  }
  return context;
}
